import { QuestionTrackingHelper } from './questionTrackingHelper';
import { getStore } from '../store';
import {
  storePreviousQuestionData as storePreviousQuestionDataAction,
  clearPreviousQuestionData,
} from '../actions/formTrackingActions';

/**
 * Helper functions for navigation tracking and current/previous question data calculation
 */

/**
 * Checks if a value is valid (not null, undefined, or empty string)
 * @param {*} value - Value to check
 * @returns {boolean} True if value is valid
 */
const isValidQuestionFieldValue = (value) => {
  return value !== null && value !== undefined && value !== '';
};

/**
 * Extracts field values with priority: user input > pre-filled > default
 * @param {Array} stepFieldIds - Array of field IDs to extract
 * @param {Array} steps - Array of step definitions
 * @param {Object} formData - Current form data
 * @returns {Object} Object with fieldId: value pairs
 */
const extractQuestionFieldValues = (stepFieldIds, steps, formData) => {
  const fieldValues = {};

  stepFieldIds.forEach((fieldId) => {
    const step = steps.find((s) => s.attributeid === fieldId);

    // Priority: user input, pre-filled, default
    let value = null;
    if (fieldId in formData && isValidQuestionFieldValue(formData[fieldId])) {
      value = formData[fieldId]; // User input or current form value
    } else if (step && isValidQuestionFieldValue(step.value)) {
      value = step.value; // Pre-filled value from step definition
    } else if (step && isValidQuestionFieldValue(step.enumdefault)) {
      value = step.enumdefault; // Default value from step definition
    }

    if (value !== null) {
      fieldValues[fieldId] = value;
    }
  });

  return fieldValues;
};

/**
 * Creates question data object from field IDs and values
 * @param {Array} stepFieldIds - Array of field IDs
 * @param {Object} fieldValues - Object with fieldId: value pairs
 * @returns {Object} Question data with questions and answers
 */
const createQuestionData = (stepFieldIds, fieldValues) => {
  return {
    questions: stepFieldIds.join(', '),
    answers:
      Object.keys(fieldValues).length > 0
        ? QuestionTrackingHelper.fromSubmittedProps(fieldValues).answers
        : null,
  };
};

/**
 * Processes steps to extract question data, handling all validation and extraction
 * @param {Array} steps - Array of step definitions
 * @param {Object} formData - Current form data
 * @returns {Object|null} Question data object or null if no valid steps
 */
const processStepsToQuestionData = (steps, formData) => {
  if (!steps || steps.length === 0) {
    return null;
  }

  const stepFieldIds = QuestionTrackingHelper.extractFieldIds(steps);
  if (stepFieldIds.length === 0) {
    return null;
  }

  const fieldValues = extractQuestionFieldValues(stepFieldIds, steps, formData);
  return createQuestionData(stepFieldIds, fieldValues);
};

/**
 * Calculates and stores previous question data for backwards navigation
 * This is called from form components before history.goBack()
 *
 * @param {Array} displayedSteps - The current step being navigated away from
 * @param {Object} formData - Current form data
 */
export const storePreviousQuestionDataForBackNavigation = (
  displayedSteps,
  formData
) => {
  const previousQuestionData = processStepsToQuestionData(
    displayedSteps,
    formData
  );
  if (previousQuestionData) {
    const store = getStore();
    store.dispatch(storePreviousQuestionDataAction(previousQuestionData));
  }
};

/**
 * Calculates previous question data for navigation
 * This is called from processTrackingEvents during form submission
 * Always includes question field IDs (since they were displayed) and answers for fields with values
 *
 * @param {Array} steps - The steps being submitted
 * @param {Object} formData - Current form data
 * @returns {Object} Previous question data object
 */
export const calculatePreviousQuestionData = (steps, formData) => {
  return (
    processStepsToQuestionData(steps, formData) || {
      questions: null,
      answers: null,
    }
  );
};

/**
 * Stores previous question data in Redux formTracking for later retrieval
 *
 * @param {Object} previousQuestionData - The calculated previous question data
 */
export const storePreviousQuestionData = (previousQuestionData) => {
  const store = getStore();
  store.dispatch(storePreviousQuestionDataAction(previousQuestionData));
};

/**
 * Calculates complete question tracking data for analytics events
 * This retrieves previous data, calculates current data, and combines them
 *
 * @param {Array} stepSet - The current step that was loaded
 * @param {Object} formData - Current form data
 * @returns {Object} Complete tracking data for analytics
 */
export const calculateQuestionTrackingData = (stepSet, formData) => {
  const store = getStore();
  const previousData = store.getState().formTracking.previousQuestionData || {
    questions: null,
    answers: null,
  };

  store.dispatch(clearPreviousQuestionData());

  let currentData = { questions: null, answers: null };

  if (stepSet && stepSet.length > 0) {
    const currentFieldIds = QuestionTrackingHelper.extractFieldIds(stepSet);

    if (currentFieldIds.length > 0) {
      const currentFormData = formData || {};
      const currentValues = QuestionTrackingHelper.processFieldValues(
        currentFieldIds,
        currentFormData
      );
      currentData = QuestionTrackingHelper.smartJoin(
        currentFieldIds,
        currentValues
      );
    }
  }

  return {
    previousquestionasked: previousData?.questions || null,
    previousquestionsanswer: previousData?.answers || null,
    currentquestionasked: currentData?.questions || null,
    currentquestionanswered: currentData?.answers || null,
  };
};
